import React, { useCallback, useRef, useState } from 'react';
import { useScrollListener } from '../../../hooks/windowEvents/useScrollListener';
import cc from 'classcat';
import { MediaWidth } from '../../Common/MediaWidth';
import PricingReminder from '../../BillingReminder';

interface IContainerWithSidebar {
    elements: ContainerElement[];
    title: string;
    titleButton?: ITitleButton;
    headerElements?: React.ReactNode;
    className?: string;
    contentClassName?: string;
}

interface ITitleButton {
    iconSrc: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export interface ContainerElement {
    id: string;
    title?: string | React.ReactNode;
    element: React.ReactNode;
}

export const ContainerWithSidebar = ({ elements, title, titleButton, headerElements, className, contentClassName }: IContainerWithSidebar) => {
    const refElements = useRef<HTMLDivElement[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isAnimatingScroll = useRef(false);

    const addElementToRef = (element: HTMLDivElement | null) => {
        if (element && !refElements.current.includes(element)) {
            refElements.current.push(element);
        }
    };

    const handleScroll = useCallback(() => {
        if (isAnimatingScroll.current) return;

        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (Math.ceil(scrollPosition) >= documentHeight) {
            setSelectedIndex(refElements.current.length - 2);
            return;
        }

        const viewportMiddle = window.scrollY + window.innerHeight / 2;
        const selectedElementIndex = refElements.current.findIndex(
            (element, index) =>
                element.offsetTop <= viewportMiddle &&
                (index === refElements.current.length - 1 || refElements.current[index + 1].offsetTop > viewportMiddle)
        );
        if (selectedElementIndex !== -1) {
            setSelectedIndex(selectedElementIndex);
        }
    }, []);

    const handleClick = (index: number) => {
        isAnimatingScroll.current = true;
        refElements.current[index].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        setSelectedIndex(index);

        setTimeout(() => (isAnimatingScroll.current = false), 1000);
    };

    useScrollListener(handleScroll);

    return (
        <div className="container-with-sidebar">
            <div className="container-with-sidebar__header">
                <div className="container-with-sidebar__left">
                    <h2>{title}</h2>
                    {titleButton && (
                        <button onClick={titleButton.onClick} className="container-with-sidebar__button">
                            <img src={titleButton.iconSrc} alt={title} />
                        </button>
                    )}
                </div>
                <div className="container-with-sidebar__right">{headerElements}</div>
            </div>
            <div className="container-with-sidebar__main">
                <div className="container-with-sidebar__sidebar">
                    {elements.map(
                        (element, i) =>
                            element.title && (
                                <a
                                    href={`#${element.id}`}
                                    key={element.id}
                                    onClick={e => {
                                        e.preventDefault();
                                        handleClick(i);
                                    }}
                                >
                                    <h4
                                        className={cc([
                                            'container-with-sidebar__title',
                                            { 'container-with-sidebar__title--selected': i === selectedIndex },
                                        ])}
                                    >
                                        {element.title}
                                    </h4>
                                </a>
                            )
                    )}
                </div>
                <div className={cc([className || 'container-with-sidebar__content-wrapper'])}>
                    <div className={cc([contentClassName || 'container-with-sidebar__content'])}>
                        {elements.map(element => (
                            <div ref={addElementToRef} key={element.id} id={element.id}>
                                {element.title && <h2>{element.title}</h2>}
                                {element.element}
                            </div>
                        ))}
                    </div>
                    <MediaWidth max="md">{headerElements}</MediaWidth>
                </div>
            </div>
        </div>
    );
};
